import { FC } from 'react';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import Skeleton from '../presentational/skeleton';
import { TwoPaneContent } from '../presentational/layouts';
import { Spinner } from '../presentational/spinner';
import { HelmetWithTitleAndDescription } from '../helmetHelper';
import { featuresSelector, siteConfigSelector } from '../../scripts/selectors/global';
import SkeletonProfileWarningSmall from './SkeletonProfileWarningSmall';
import SkeletonProfileSection from './SkeletonProfileSection';
import SkeletonGearAndTalents from './SkeletonGearAndTalents';
import SkeletonCharacterRaidProgression from './SkeletonCharacterRaidProgression';
import SkeletonCharacterMythicPlusProgression from './SkeletonCharacterMythicPlusProgression';

// Inline selectors for character details state
const characterSelector = (state: any) => state?.characterDetails?.character;
const characterCustomizationsSelector = (state: any) => state?.characterDetails?.characterCustomizations;
const characterUserSelector = (state: any) => state?.characterDetails?.user;
const characterTeamSelector = (state: any) => state?.characterDetails?.team;

const SkeletonCharacterLayout: FC<any> = (props) => {
  const location = useLocation();
  const siteConfig = useSelector(siteConfigSelector);
  const currentFeatures = useSelector(featuresSelector);
  const character = useSelector(characterSelector);
  const characterCustomizations = useSelector(characterCustomizationsSelector);
  const user = useSelector(characterUserSelector);
  const team = useSelector(characterTeamSelector);
  const characterRecruitment = /recruitment/g.test(location.pathname);
  const characterDetails = !characterRecruitment;

  const hasRecruitment = currentFeatures.recruitment_view && character?.recruitmentProfiles?.length > 0;
  const hasUser = user || team || hasRecruitment;

  if (characterDetails && siteConfig.isEmbedded) {
    return <></>;
  }

  return (
    <div>
      <HelmetWithTitleAndDescription
        title="Character Profile - World of Warcraft (WoW)"
        description="View any World of Warcraft (WoW) character's raid progress, Mythic+ progress, equipped items, talents, and more."
      />

      <Skeleton height={144}>
        <div style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
          <div style={{ margin: '0 16px' }}>
            <Skeleton variant="item" width={80} height={80} animate />
          </div>
          <div style={{ height: 'max-content' }}>
            <Skeleton
              variant="item"
              width={180}
              height={25}
              style={{ marginBottom: 4, borderRadius: '0.25rem' }}
              animate
            />
            <Skeleton
              variant="item"
              width={150}
              height={14}
              style={{ marginBottom: 4, borderRadius: '0.25rem' }}
              animate
            />
            <Skeleton
              variant="item"
              width={150}
              height={14}
              style={{ marginBottom: 4, borderRadius: '0.25rem' }}
              animate
            />
            <Skeleton
              variant="item"
              width={150}
              height={14}
              style={{ marginBottom: 4, borderRadius: '0.25rem' }}
              animate
            />
          </div>
        </div>
      </Skeleton>

      <TwoPaneContent>
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex' }}>
              {hasUser && <Skeleton width={91} height={40} margin="0 4px 0 0" borderRadius="0.25rem" />}
              {hasRecruitment && <Skeleton width={109} height={40} margin="0 4px 0 0" borderRadius="0.25rem" />}
            </div>
            <div style={{ display: 'flex' }}>
              {user && <Skeleton width={119} height={32} margin="0 0 0 8px" borderRadius="0.25rem" />}
              {team && <Skeleton width={134} height={32} margin="0 0 0 8px" borderRadius="0.25rem" />}
            </div>
          </div>

          <SkeletonProfileWarningSmall />

          <SkeletonProfileSection currentFeatures={currentFeatures} characterCustomizations={characterCustomizations} />

          <SkeletonGearAndTalents />

          {characterDetails && (
            <div className="rio-p-horizontal-mobi--x-small">
              <SkeletonCharacterRaidProgression />

              <SkeletonCharacterMythicPlusProgression />
            </div>
          )}

          {characterRecruitment && (
            <Skeleton height="100%" style={{ position: 'relative' }}>
              <Spinner inline />
            </Skeleton>
          )}
        </>
        <>
          {currentFeatures.admin && (
            <div>
              <Skeleton height={23} />
              <Skeleton variant="item" height={30} animate />
            </div>
          )}

          <div style={{ marginTop: 24 }}>
            <Skeleton height={23} />
            <Skeleton variant="item" height={183} animate />
          </div>

          <div style={{ marginTop: 24 }}>
            <Skeleton height={23} />
            <Skeleton variant="item" height={288} animate />
          </div>
        </>
      </TwoPaneContent>
    </div>
  );
};

export default SkeletonCharacterLayout;
